import { memo } from "react";
import { Col, Row, Table } from "react-bootstrap";
import { formattedValue, isValidDate } from "../../Services/common";
import { changeDateFormatForDisplay } from "../../Utils/dateUtils";
import { slice } from "../../Utils/stringUtils";
import IconResource from "../../Wrapper/IconResource/IconResource";
import ImageResource from "../../Wrapper/ImageResource/ImageResource";
import AnimatedPage from "../AnimatedPage/AnimatedPage";
import DateRangePicker from "../_widgets/DateRangePicker/DateRangePicker";
import Input from "../_widgets/Input/Input";
import MainLoader from "../_widgets/Loader/MainLoader";
import MultiSelectDropdown from "../_widgets/MultiSelect/MultiSelectDropdown";
import NavBar from "../_widgets/NavBar/NavBar";
import CustomPagination from "../_widgets/Pagination/Pagination";
import Text from "../_widgets/Text/Text";
import CustomTooltip from "../_widgets/Tooltip/CustomTooltip";
import EditCreditNote from "./EditCreditNotes";
import "./CreditNotes.scss";

const CreditNotesUi = memo(
  ({
    searchQuery,
    setSearchQuery,
    dates,
    setDates,
    datePickerEndRef,
    datePickerStartRef,
    loading,
    onPageChange,
    getCreditNotesList,
    creditNotesList,
    modalShow,
    handleClose,
    handleSave,
    buttonLoading,
    modalLoading,
    selectLoading,
    creditNoteData,
    setCreditNoteData,
    fetchCreditCategories,
    getCreditCategoryList,
    setCategorySearch,
    setModalShow,
    openDetailsPage,
    statusOptions,
    statusFilter,
    handleStatusFilterChange,
  }) => {
    const formatDateTime = (dateStr) => {
      if (!dateStr || !isValidDate(dateStr)) return "";
      const d = new Date(dateStr);
      return (
        d.toLocaleDateString("en-IN", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        }) +
        ", " +
        d.toLocaleTimeString("en-IN", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        })
      );
    };

    return (
      <>
        <EditCreditNote
          modelShow={modalShow}
          handleClose={handleClose}
          handleSave={handleSave}
          buttonLoading={buttonLoading}
          loading={modalLoading}
          selectLoading={selectLoading}
          creditNoteData={creditNoteData}
          setCreditNoteData={setCreditNoteData}
          fetchCreditCategories={fetchCreditCategories}
          getCreditCategoryList={getCreditCategoryList}
          setCategorySearch={setCategorySearch}
        />

        <AnimatedPage>
          <NavBar
            heading="Credit Notes"
            onClick={() => {
              document.body.classList.toggle("side_active");
              document.body.classList.toggle("side_active_mb");
            }}
            Icon="menu"
          />

          <div className="patient_list white_card credit-notes-container">
            <div className="top_bar">
              <Text type="h2" text={""} />
              <div className="d-flex align-items-start align-items-md-center gap-3">
                <div className="bg_grey d-flex align-items-center justify-content-center position-relative px-2 py-1">
                  {statusFilter.length > 0 ? (
                    <div className="round-notification">{statusFilter.length}</div>
                  ) : (
                    ""
                  )}

                  <MultiSelectDropdown
                    items={statusOptions}
                    selectedOptions={statusFilter}
                    onChange={handleStatusFilterChange}
                    name={
                      <div className="d-flex align-items-center gap-1">
                        <IconResource type="filter" size={12} color={"var(--primary)"} />
                        <small
                          style={{
                            fontSize: "12px",
                            fontWeight: "bold",
                            color: "var(--primary)",
                            cursor: "pointer",
                          }}
                        >
                          Status
                        </small>
                        {statusFilter.length > 0 && (
                          <IconResource
                            type={"cross"}
                            size={12}
                            onClick={() => handleStatusFilterChange([])}
                          />
                        )}
                      </div>
                    }
                  />
                </div>

                <Row className="search_filter">
                  <Col lg={4}>
                    <Input
                      placeholder="Search"
                      id="searchInput"
                      suffixIcon="search"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onClickIconSuff={() => getCreditNotesList()}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          getCreditNotesList();
                        }
                      }}
                      iconTooltip="Search"
                    />
                  </Col>
                  <Col lg={8}>
                    <DateRangePicker
                      startDate={dates?.startDate}
                      setStartDate={(startDate) => setDates({ ...dates, startDate })}
                      endDate={dates?.endDate}
                      setEndDate={(endDate) => setDates({ ...dates, endDate })}
                      onChange={(startDate, endDate) => setDates({ startDate, endDate })}
                      datePickerStartRef={datePickerStartRef}
                      datePickerEndRef={datePickerEndRef}
                      showDateShortcuts={true}
                    />
                  </Col>
                </Row>
              </div>
            </div>

            <div className="custom_table position-relative cn-table-wrapper">
              {loading ? (
                <MainLoader />
              ) : creditNotesList?.data?.length > 0 ? (
                <>
                  <Table responsive hover>
                    <thead>
                      <tr>
                        <th>Bill Info</th>
                        <th style={{ width: "24%" }}>Medicine & Supplier</th>
                        <th className="text-center">Quantities</th>
                        <th>CN Details</th>
                        <th>CN Amount</th>
                        <th>Status</th>
                        <th>Remarks</th>
                        <th className="text-end">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {creditNotesList?.data?.map((data) => (
                        <tr
                          key={data.id}
                          onClick={(e) => {
                            e.stopPropagation();
                            openDetailsPage(data.bill_no);
                          }}
                        >
                          <td>
                            <span className="cn-bill-badge">#{data.bill_no || "-"}</span>
                            <div className="text-muted small mt-1">
                              <i className="fa fa-calendar me-1"></i>
                              {isValidDate(data.product_detail?.bill_date)
                                ? changeDateFormatForDisplay(data.product_detail?.bill_date)
                                : "-"}
                            </div>
                          </td>
                          <td className="word-break" style={{ minWidth: "200px" }}>
                            <CustomTooltip text={data.product?.name} placement="top">
                              <div className="cn-med-name">{data.product?.name || "-"}</div>
                            </CustomTooltip>
                            <div className="d-flex align-items-center gap-2 flex-wrap mt-1">
                              <span className="cn-batch-pill">
                                <i className="fa fa-tag me-1 text-primary"></i>
                                Batch: {data?.product_detail?.batch_number || "-"}
                              </span>
                              {data?.product_detail?.supplier?.name && (
                                <span className="cn-supplier-tag">
                                  <i className="fa fa-building-o me-1 text-secondary"></i>
                                  {slice(data?.product_detail?.supplier?.name, 25)}
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="text-center" style={{ minWidth: "140px" }}>
                            <div className="d-flex flex-column align-items-center gap-1">
                              <span className="cn-qty-badge cn-qty-purchased">
                                Purchased <strong>{(data?.product_detail?.quantity ?? 0).toLocaleString()}</strong>
                              </span>
                              <span className="cn-qty-badge cn-qty-returned">
                                Returned <strong>{(data?.quantity ?? 0).toLocaleString()}</strong>
                              </span>
                            </div>
                          </td>
                          <td>
                            <div className="cn-number-badge">{data.cn_number || "-"}</div>
                            {(data.created_by?.name || data.created_at) ? (
                              <div className="cn-audit-meta">
                                <span>{data.created_by?.name ? `By: ${data.created_by.name}` : "Created"}</span>
                                {data.created_at || data.return_date ? (
                                  <span>• {formatDateTime(data.created_at || data.return_date)}</span>
                                ) : ""}
                              </div>
                            ) : isValidDate(data.return_date) ? (
                              <div className="cn-date-text">
                                {changeDateFormatForDisplay(data.return_date)}
                              </div>
                            ) : null}
                          </td>
                          <td>
                            <span className="cn-amount-val">{formattedValue(data.cn_amount, true, true)}</span>
                          </td>
                          <td>
                            <span className={`cn-status-pill ${data.status?.toLowerCase() === "settled" ? "settled" : "open"}`}>
                              <span className="cn-dot"></span>
                              {data.status || "OPEN"}
                            </span>
                            {data.status === "SETTLED" && (data.settled_by?.name || data.settled_at) && (
                              <div className="cn-audit-meta text-success">
                                <i className="fa fa-check-circle"></i>
                                <span>{data.settled_by?.name ? `Settled by: ${data.settled_by.name}` : "Settled"}</span>
                                {data.settled_at || data.updated_at ? <span>• {formatDateTime(data.settled_at || data.updated_at)}</span> : ""}
                              </div>
                            )}
                          </td>
                          <td>
                            <CustomTooltip text={data.settled_remark || data.remark} placement="top">
                              <span className="text-secondary">{slice(data.settled_remark || data.remark, 18) || "-"}</span>
                            </CustomTooltip>
                          </td>
                          <td className="text-end">
                            <CustomTooltip text="Edit Credit Note" placement={"top"}>
                              <button
                                type="button"
                                className="cn-action-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setCreditNoteData(data);
                                  setModalShow(true);
                                }}
                              >
                                {buttonLoading === `edit${data.id}` ? (
                                  <IconResource type="loader" size={14} />
                                ) : (
                                  <IconResource type="edit" size={14} color="var(--primary)" />
                                )}
                              </button>
                            </CustomTooltip>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>

                  <CustomPagination
                    activePage={creditNotesList?.current_page}
                    onPageChange={onPageChange}
                    pageLength={creditNotesList?.per_page}
                    totalData={creditNotesList?.total}
                    lastPage={creditNotesList?.last_page}
                  />
                </>
              ) : (
                <div className="no_data position-relative">
                  <ImageResource type="noData" style={{ width: "300px" }} className="img-fluid" />
                  <Text type="h2" text="There is no data to display. Try re-setting filters!" />
                </div>
              )}
            </div>
          </div>
        </AnimatedPage>
      </>
    );
  },
);

export default CreditNotesUi;
